import React, { useState, useEffect, createContext, useCallback, useContext } from 'react';
import { IntlProvider } from 'react-intl';
import PropTypes from 'prop-types';
import Polyglot from 'node-polyglot';

import { fetchPhrases } from '../../utils/languageHelper';

// System and Browser preferences:
const CURRENT_LANGUAGE = window.navigator.language || window.navigator.userLanguage;
const BROWSER_PREFS_LANGUAGES = typeof window.navigator.languages === 'undefined' ? ['es-419'] : window.navigator.languages;

const LocalizationContext = createContext({
  locale: 'es-419', // Default locale
  translate: (key) => key, // Default translate function
  changeLocale: () => { }, // Default changeLocale function
  changeDefaultLocale: () => { }, // Default changeDefaultLocale function
  usingFallback: false,
});

export const useLocalization = () => useContext(LocalizationContext);
/**
 * Component provides translate functions
 * to enable internationalization
 *
 * @class I18nProvide
 */
const I18nProvider = ({
  forceLocale,
  clientPath,
  supportedLanguages,
  languageMap,
  parameters,
  children,
  fallbackLocale = 'es-419' }) => {
  const [defaultLocale, setDefaultLocale] = useState(fallbackLocale);
  const [localePath, setLocalePath] = useState(clientPath);
  const urlParameters = new URLSearchParams(parameters);
  const [locale, setLocale] = useState(() => {
    let initialLocale = forceLocale;
    if (!initialLocale) {
      initialLocale = CURRENT_LANGUAGE === BROWSER_PREFS_LANGUAGES[0]
        ? CURRENT_LANGUAGE : BROWSER_PREFS_LANGUAGES[0];
    }
    return initialLocale;
  });
  const [usingFallback, setUsingFallback] = useState(false);
  const [messages, setMessages] = useState(null);
  const changeLocale = useCallback((newLocale) => {
    if (locale === newLocale) {
      return;
    }
    setLocale(newLocale);
  }, [locale, setLocale]);
  const changeDefaultLocale = useCallback((newFallbackLocale) => {
    if (defaultLocale === newFallbackLocale) {
      return;
    }
    setDefaultLocale(newFallbackLocale);
  }, [defaultLocale, setDefaultLocale]);
  const changeLocalePath = useCallback((newLocalePath) => {
    if (localePath === newLocalePath) {
      return;
    }
    setLocalePath(newLocalePath);
  }, [localePath, setLocalePath]);

  useEffect(() => {
    const country = urlParameters?.get('country');
    let updatedLocale = locale;
    let isFallback = false;

    // Verify if the language is supported, otherwise use fallback
    if (!updatedLocale || supportedLanguages.indexOf(updatedLocale) === -1) {
      updatedLocale = defaultLocale;
      isFallback = true;
    }

    // Apply fallback for unsupported variants of the fallback language
    if (updatedLocale && updatedLocale.startsWith(defaultLocale + '-') &&
      supportedLanguages.indexOf(updatedLocale) === -1 && supportedLanguages.indexOf(defaultLocale) !== -1) {
      updatedLocale = defaultLocale;
    }

    // Apply language based on country parameter if available
    if (languageMap?.hasOwnProperty(country)) {
      updatedLocale = languageMap[country.toUpperCase()];
    }

    fetchPhrases(updatedLocale, localePath)
      .then((data) => {
        setMessages(data);
        setLocale(updatedLocale);
        setUsingFallback(isFallback);
        document.documentElement.lang = updatedLocale;
      })
      .catch((e) => {
        console.log('fetchPhrases fail -> fallbackLocale', e.message);
        return fetchPhrases(defaultLocale, localePath)
          .then((data) => {
            setMessages(data);
            setLocale(defaultLocale);
            setUsingFallback(true);
            document.documentElement.lang = defaultLocale;
          })
      });
  }, [locale, localePath, supportedLanguages, languageMap, parameters, defaultLocale]);

  const contextValue = {
    locale,
    translate: messages ? new Polyglot({ locale, phrases: messages }).t.bind(new Polyglot({ locale, phrases: messages })) : ((key) => key),
    changeLocale,
    changeDefaultLocale,
    changeLocalePath,
    usingFallback,
  };

  return (
    <LocalizationContext.Provider value={contextValue}>
        <IntlProvider locale={locale} messages={messages}>
          {React.Children.only(children)}
        </IntlProvider>
    </LocalizationContext.Provider>
  );
}

export const WithLocalize = (BaseComponent) => {
  const LocalizedComponent = (props) => {
    const { translate, locale, changeLocale, changeDefaultLocale, changeLocalePath } = useLocalization();

    return (
      <BaseComponent
        translate={translate}
        locale={locale}
        changeLocale={changeLocale}
        changeDefaultLocale={changeDefaultLocale}
        changeLocalePath={changeLocalePath}
        {...props}
      />
    );
  };

  // No need for contextTypes anymore!

  return LocalizedComponent;
};

I18nProvider.propTypes = {
  forceLocale: PropTypes.string,
  clientPath: PropTypes.string.isRequired,
  supportedLanguages: PropTypes.array.isRequired,
  languageMap: PropTypes.object,
  parameters: PropTypes.object,
  children: PropTypes.node.isRequired,
  fallbackLocale: PropTypes.string,
};

export default I18nProvider;
